import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getWmsAlmacenes, createWmsAlmacen, updateWmsAlmacen, getWmsPasillosByAlmacen, createWmsPasillo } from '../../api.wms';
import WmsSidebar from '../../components/WmsSidebar';
import WmsTopbar from '../../components/wms_topbar';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// Utilidades para geometría de lados y área
function getPolygonPointsFromLados(lados) {
  if (!Array.isArray(lados) || lados.length < 2) return [];
  let points = [{ x: 0, y: 0 }];
  let anguloAcumulado = 0;
  lados.forEach((lado, i) => {
    anguloAcumulado += (i === 0 ? 0 : lado.angulo || 0);
    const rad = (Math.PI / 180) * anguloAcumulado;
    const prev = points[points.length - 1];
    const x = prev.x + (lado.longitud || 0) * Math.cos(rad);
    const y = prev.y + (lado.longitud || 0) * Math.sin(rad);
    points.push({ x, y });
  });
  // Cerrar polígono si corresponde
  if (lados.length > 2) points[points.length - 1] = points[0];
  return points;
}

function calcularAreaPoligono(points) {
  if (!Array.isArray(points) || points.length < 3) return 0;
  let area = 0;
  for (let i = 0; i < points.length - 1; i++) {
    area += (points[i].x * points[i + 1].y) - (points[i + 1].x * points[i].y);
  }
  return Math.abs(area / 2);
}

const WmsAlmacenes = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState('almacenes');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [almacenes, setAlmacenes] = useState([]);
  const [form, setForm] = useState({
    nombre: '',
    codigo: '',
    tipo: 'Rack',
    direccion: '',
    ciudad: '',
    estado: '',
    pais: '',
    lados: [], // Si se requiere geometría
  });
  const [showForm, setShowForm] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editAlmacen, setEditAlmacen] = useState(null);
  const [editTab, setEditTab] = useState(0);
  const [editPasillos, setEditPasillos] = useState([]);
  const [newPasilloOpen, setNewPasilloOpen] = useState(false);
  const [newPasillo, setNewPasillo] = useState({ numero: '', ubicaciones_por_pasillo: 1, niveles: 1, posiciones_por_nivel: 1 });

  React.useEffect(() => {
    const fetchAlmacenes = async () => {
      try {
        const data = await getWmsAlmacenes();
        setAlmacenes(data);
      } catch (error) {
        setAlmacenes([]);
      }
    };
    fetchAlmacenes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Solo enviar los campos válidos
      const payload = {
        nombre: form.nombre,
        codigo: form.codigo,
        tipo: form.tipo,
        direccion: form.direccion,
        ciudad: form.ciudad,
        estado: form.estado,
        pais: form.pais,
        lados: form.lados,
      };
      await createWmsAlmacen(payload);
      setShowForm(false);
      // Refrescar lista
      const data = await getWmsAlmacenes();
      setAlmacenes(data);
      setForm({ nombre: '', codigo: '', tipo: 'Rack', direccion: '', ciudad: '', estado: '', pais: '', lados: [] });
    } catch (error) {
      alert('Error al crear almacén: ' + (error?.response?.data?.detail || error.message));
    }
  };

  const handleOpenEditModal = async (almacen) => {
    setEditAlmacen({ ...almacen });
    setEditModalOpen(true);
    // Cargar pasillos del almacén
    try {
      const pasillos = await getWmsPasillosByAlmacen(almacen.id);
      setEditPasillos(pasillos);
    } catch {
      setEditPasillos([]);
    }
  };
  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditAlmacen(null);
  };
  const handleEditAlmacenChange = (field, value) => {
    setEditAlmacen({ ...editAlmacen, [field]: value });
  };
  const handleEditPasilloChange = (idx, field, value) => {
    const updated = editAlmacen.propiedades_pasillos.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    setEditAlmacen({ ...editAlmacen, propiedades_pasillos: updated });
  };
  const handleAddEditPasillo = () => {
    setEditAlmacen({
      ...editAlmacen,
      propiedades_pasillos: [
        ...(editAlmacen.propiedades_pasillos || []),
        { nombre: '', ubicacionesPorPasillo: 1, niveles: 1, posicionesPorNivel: 1, ocupadas: 0 }
      ]
    });
  };
  const handleRemoveEditPasillo = (idx) => {
    setEditAlmacen({
      ...editAlmacen,
      propiedades_pasillos: editAlmacen.propiedades_pasillos.filter((_, i) => i !== idx)
    });
  };
  const handleSaveEditAlmacen = async () => {
    try {
      await updateWmsAlmacen(editAlmacen.id, editAlmacen);
      setAlmacenes(almacenes.map(a => a.id === editAlmacen.id ? editAlmacen : a));
      setEditModalOpen(false);
    } catch (error) {
      alert('Error al actualizar el almacén: ' + (error?.response?.data?.detail || error.message));
    }
  };
  const handleCreatePasillo = async () => {
    try {
      await createWmsPasillo({ ...newPasillo, almacen_id: editAlmacen.id });
      const pasillos = await getWmsPasillosByAlmacen(editAlmacen.id);
      setEditPasillos(pasillos);
      setNewPasilloOpen(false);
      setNewPasillo({ numero: '', ubicaciones_por_pasillo: 1, niveles: 1, posiciones_por_nivel: 1 });
    } catch (error) {
      alert('Error al crear pasillo: ' + (error?.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="flex">
      {/* Sidebar drawer solo en móvil */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}
      <div
        className={`fixed z-50 inset-y-0 left-0 transition-transform duration-300 md:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: '256px', minHeight: '100vh' }}
      >
        <WmsSidebar activeItem={activeItem} setActiveItem={setActiveItem} />
      </div>
      {/* Sidebar estático solo en desktop, en el flujo normal */}
      <div className="hidden md:flex">
        <WmsSidebar activeItem={activeItem} setActiveItem={setActiveItem} />
      </div>
      <div className="flex-1 bg-gray-100 min-h-screen md:p-6">
        <WmsTopbar rightContent={
          <button
            className="md:hidden p-2 ml-2"
            onClick={() => setSidebarOpen(true)}
            aria-label="Abrir menú"
            style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
          >
            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="feather feather-menu"><line x1="3" y1="12" x2="25" y2="12"/><line x1="3" y1="6" x2="25" y2="6"/><line x1="3" y1="18" x2="25" y2="18"/></svg>
          </button>
        } />
        <h1 className="text-2xl font-bold mb-4">Gestión de Almacenes</h1>
        <p className="mb-6">Administra los almacenes y configura su estructura para generar ubicaciones automáticamente.</p>
        <button
          className="mb-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => navigate('/wms/nueva-almacen')}
        >
          Nuevo Almacén
        </button>
        {showForm && (
          <form className="bg-white p-4 rounded shadow mb-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block font-semibold">Nombre</label>
                <input type="text" name="nombre" value={form.nombre} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Código</label>
                <input type="text" name="codigo" value={form.codigo} onChange={handleChange} required className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Tipo</label>
                <select name="tipo" value={form.tipo} onChange={handleChange} className="w-full border rounded px-2 py-1">
                  <option value="Rack">Rack</option>
                  <option value="Bin">Bin</option>
                  <option value="Zona">Zona</option>
                </select>
              </div>
              <div>
                <label className="block font-semibold">Dirección</label>
                <input type="text" name="direccion" value={form.direccion} onChange={handleChange} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Ciudad</label>
                <input type="text" name="ciudad" value={form.ciudad} onChange={handleChange} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">Estado</label>
                <input type="text" name="estado" value={form.estado} onChange={handleChange} className="w-full border rounded px-2 py-1" />
              </div>
              <div>
                <label className="block font-semibold">País</label>
                <input type="text" name="pais" value={form.pais} onChange={handleChange} className="w-full border rounded px-2 py-1" />
              </div>
              {/* Si se requiere, agregar aquí el input para lados (geometría) */}
            </div>
            <div className="mt-4">
              <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Guardar almacén</button>
            </div>
          </form>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded shadow">
            <thead>
              <tr>
                <th className="px-4 py-2">Nombre</th>
                <th className="px-4 py-2">Código</th>
                <th className="px-4 py-2">Tipo</th>
                <th className="px-4 py-2">Dirección</th>
                <th className="px-4 py-2">Ciudad</th>
                <th className="px-4 py-2">Estado</th>
                <th className="px-4 py-2">País</th>
                <th className="px-4 py-2">Lados</th>
                <th className="px-4 py-2">Área (m²)</th>
                <th className="px-4 py-2">Cantidad de Pasillos</th>
                <th className="px-4 py-2">% Utilización</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {almacenes.map((alm) => {
                // Calcular área si hay lados
                let area = 'N/A';
                let ladosResumen = 'N/A';
                let cantidadPasillos = '-';
                let porcentajeUtilizacion = '-';
                // Calcular cantidad de pasillos y porcentaje de utilización
                if (Array.isArray(alm.propiedades_pasillos) && alm.propiedades_pasillos.length > 0) {
                  cantidadPasillos = alm.propiedades_pasillos.length;
                  // Calcular total y ocupadas
                  let totalUbicaciones = 0;
                  let totalOcupadas = 0;
                  alm.propiedades_pasillos.forEach(p => {
                    const t = (parseInt(p.ubicacionesPorPasillo) || 0) * (parseInt(p.niveles) || 0) * (parseInt(p.posicionesPorNivel) || 0);
                    totalUbicaciones += t;
                    totalOcupadas += p.ocupadas ? parseInt(p.ocupadas) : 0;
                  });
                  porcentajeUtilizacion = totalUbicaciones > 0 ? Math.round((totalOcupadas / totalUbicaciones) * 100) + '%' : '0%';
                }
                if (Array.isArray(alm.lados) && alm.lados.length > 2) {
                  const points = getPolygonPointsFromLados(alm.lados);
                  area = calcularAreaPoligono(points).toFixed(2);
                  const totalMetros = alm.lados.reduce((acc, l) => acc + (l.longitud || 0), 0);
                  ladosResumen = `${alm.lados.length} lados (${totalMetros.toFixed(1)} m)`;
                } else if (Array.isArray(alm.lados)) {
                  ladosResumen = `${alm.lados.length} lado(s)`;
                }
                return (
                  <tr key={alm.id} className="border-t hover:bg-blue-50 cursor-pointer" onClick={() => navigate(`/wms/almacenes/${alm.id}`)}>
                    <td className="px-4 py-2">{alm.nombre}</td>
                    <td className="px-4 py-2">{alm.codigo}</td>
                    <td className="px-4 py-2">{alm.tipo || '-'}</td>
                    <td className="px-4 py-2">{alm.direccion || '-'}</td>
                    <td className="px-4 py-2">{alm.ciudad || '-'}</td>
                    <td className="px-4 py-2">{alm.estado || '-'}</td>
                    <td className="px-4 py-2">{alm.pais || '-'}</td>
                    <td className="px-4 py-2">{ladosResumen}</td>
                    <td className="px-4 py-2 font-bold">{area}</td>
                    <td className="px-4 py-2">{cantidadPasillos}</td>
                    <td className="px-4 py-2">{porcentajeUtilizacion}</td>
                    <td className="px-4 py-2">
                      <Button variant="outlined" size="small" onClick={e => { e.stopPropagation(); handleOpenEditModal(alm); }}>Editar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Modal open={editModalOpen} onClose={handleCloseEditModal}>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, maxWidth: 600, mx: 'auto', my: 8 }}>
            {editAlmacen && (
              <>
                <Tabs value={editTab} onChange={(_, v) => setEditTab(v)} sx={{ mb: 2 }}>
                  <Tab label="Información" />
                  <Tab label="Pasillos" />
                </Tabs>
                {editTab === 0 && (
                  <div>
                    <TextField label="Nombre" fullWidth margin="dense" value={editAlmacen.nombre} onChange={e => handleEditAlmacenChange('nombre', e.target.value)} />
                    <TextField label="Código" fullWidth margin="dense" value={editAlmacen.codigo} onChange={e => handleEditAlmacenChange('codigo', e.target.value)} />
                    <TextField label="Dirección" fullWidth margin="dense" value={editAlmacen.direccion} onChange={e => handleEditAlmacenChange('direccion', e.target.value)} />
                    <TextField label="Ciudad" fullWidth margin="dense" value={editAlmacen.ciudad} onChange={e => handleEditAlmacenChange('ciudad', e.target.value)} />
                    <TextField label="Estado" fullWidth margin="dense" value={editAlmacen.estado} onChange={e => handleEditAlmacenChange('estado', e.target.value)} />
                    <TextField label="País" fullWidth margin="dense" value={editAlmacen.pais} onChange={e => handleEditAlmacenChange('pais', e.target.value)} />
                  </div>
                )}
                {editTab === 1 && (
                  <div>
                    <div className="font-semibold mb-1">Pasillos</div>
                    {editPasillos.length === 0 && <div className="text-gray-500 text-sm mb-2">No hay pasillos registrados para este almacén.</div>}
                    <Button variant="outlined" size="small" onClick={() => setNewPasilloOpen(true)} className="mb-2">Crear nuevo pasillo</Button>
                    {editPasillos.map((pasillo, idx) => {
                      return (
                        <div key={pasillo.id} className="flex flex-col md:flex-row gap-2 items-center border rounded p-2 mb-2 bg-gray-50">
                          <div className="flex-1">
                            <div className="font-semibold">{pasillo.numero}</div>
                            <div className="text-xs text-gray-600">Niveles: {pasillo.niveles}</div>
                            <div className="text-xs text-gray-600">Posiciones por nivel: {pasillo.posiciones_por_nivel}</div>
                            <div className="text-xs text-gray-600">% Utilización: (próximamente)</div>
                          </div>
                        </div>
                      );
                    })}
                    <Dialog open={newPasilloOpen} onClose={() => setNewPasilloOpen(false)}>
                      <DialogTitle>Crear nuevo pasillo</DialogTitle>
                      <DialogContent>
                        <TextField label="Número de pasillo" type="number" fullWidth margin="dense" value={newPasillo.numero} onChange={e => setNewPasillo({ ...newPasillo, numero: e.target.value })} />
                        <TextField label="Ubicaciones por pasillo" type="number" fullWidth margin="dense" value={newPasillo.ubicaciones_por_pasillo} onChange={e => setNewPasillo({ ...newPasillo, ubicaciones_por_pasillo: e.target.value })} />
                        <TextField label="Niveles" type="number" fullWidth margin="dense" value={newPasillo.niveles} onChange={e => setNewPasillo({ ...newPasillo, niveles: e.target.value })} />
                        <TextField label="Posiciones por nivel" type="number" fullWidth margin="dense" value={newPasillo.posiciones_por_nivel} onChange={e => setNewPasillo({ ...newPasillo, posiciones_por_nivel: e.target.value })} />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setNewPasilloOpen(false)}>Cancelar</Button>
                        <Button variant="contained" onClick={handleCreatePasillo}>Crear</Button>
                      </DialogActions>
                    </Dialog>
                  </div>
                )}
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleSaveEditAlmacen}>Guardar cambios</Button>
                <Button variant="text" sx={{ mt: 2, ml: 2 }} onClick={handleCloseEditModal}>Cancelar</Button>
              </>
            )}
          </Box>
        </Modal>
      </div>
    </div>
  );
};

export default WmsAlmacenes;
